/**
 * The onboarding API client.
 *
 * Onboarding talks to the V2 surface (`/v2/auth/*`, `/v2/onboarding/*`), which
 * is a different contract from the rest of the app, so it gets its own client
 * rather than routing through `helpers/apiClient`. Two reasons that matters:
 *
 *   1. `baseFetch` attaches `localStorage.token` — the *app* session. The token
 *      minted by `/v2/auth/register` is a restricted, onboarding-scoped session
 *      (`scope: "onboarding"`, version 2). Putting it in the app's session slot
 *      would leave a half-registered member looking signed in to the rest of
 *      the app while the account is still in the onboarding state.
 *
 *   2. Until `/complete` succeeds there is no account worth resuming. The token
 *      lives in sessionStorage beside the flow state, so a refresh mid-flow
 *      still works, and it is promoted to the real session only on completion.
 *
 * Previously these screens called `/v1/web/onboarding/*` with a bare `fetch`
 * and no base URL, which only ever resolved because the MSW worker intercepted
 * them. Those endpoints were never built server-side. See
 * `docs/onboarding-redesign-decisions.md`.
 */

import { startExistingAccountFlow } from "./state/persistence";

export const ONBOARDING_TOKENS_KEY = "echo.onboarding.tokens";

let onboardingSessionGeneration = 0;
let onboardingRefreshPromise = null;
let onboardingOtpGeneration = 0;
let onboardingOtpPromise = null;

// Changes when an onboarding session starts or ends, not when its tokens are
// refreshed.
let onboardingSessionId = 0;
let finishPromise = null;

/** The three policies a member must accept, and the backend's `policyTypes`. */
export const POLICY_TYPES = ["terms", "privacy", "community"];

/* ---------------------------------------------------------------- tokens -- */

export const readOnboardingTokens = () => {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_TOKENS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // A private-mode or quota failure must not break signup: the member can
    // still finish the flow in one sitting without a persisted token.
    return null;
  }
};

const persistOnboardingTokens = (tokens) => {
  try {
    sessionStorage.setItem(ONBOARDING_TOKENS_KEY, JSON.stringify(tokens));
    return true;
  } catch {
    return false;
  }
};

const writeOnboardingTokens = (tokens, { invalidatesOtp = false } = {}) => {
  onboardingSessionGeneration += 1;
  onboardingRefreshPromise = null;
  if (invalidatesOtp) {
    onboardingOtpGeneration += 1;
    onboardingOtpPromise = null;
  }
  persistOnboardingTokens(tokens);
};

const writeOnboardingTokenMetadata = (metadata) => {
  const tokens = readOnboardingTokens();
  if (!tokens) return false;

  return persistOnboardingTokens({ ...tokens, ...metadata });
};

/**
 * Store the tokens of a session that has just begun: a registration, or a
 * login into an unfinished onboarding.
 */
export const startOnboardingSession = (tokens) => {
  onboardingSessionId += 1;
  finishPromise = null;
  writeOnboardingTokens(tokens, { invalidatesOtp: true });
};

export const clearOnboardingTokens = () => {
  onboardingSessionId += 1;
  onboardingSessionGeneration += 1;
  onboardingRefreshPromise = null;
  onboardingOtpGeneration += 1;
  onboardingOtpPromise = null;
  try {
    sessionStorage.removeItem(ONBOARDING_TOKENS_KEY);
  } catch {
    /* non-fatal */
  }
};

/** `POST /v2/auth/login` — returns either an app or onboarding session. */
export const loginAccount = async ({ email, password }) => {
  const data = await call("/v2/auth/login", {
    method: "POST",
    body: { email, password },
  });

  if (data?.onboardingRequired) {
    if (!data?.tokens?.access?.token) {
      throw new OnboardingApiError("Login returned no onboarding session", 500);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    // Pair the draft with the authenticated account before OTP delivery can
    // fail and leave the new session available for a later resume.
    startExistingAccountFlow({
      email: data.account?.email ?? email,
      emailVerified: !data.verificationRequired,
    });
    startOnboardingSession(data.tokens);

    if (data.verificationRequired) {
      await resendEmailOtp();
    }
  } else if (!promoteToAppSession(data)) {
    throw new OnboardingApiError("Login returned no app session", 500);
  }

  return data;
};

/**
 * Hand the onboarding session over to the app.
 *
 * Called only with the result from `/complete`. Completion revokes the scoped
 * refresh token and returns a normal V2 session, so promoting the tokens that
 * were used to make the request would leave the member with a broken session.
 */
export const promoteToAppSession = ({ account, tokens } = {}) => {
  if (!tokens?.access?.token) return false;
  localStorage.setItem("token", tokens.access.token);
  if (tokens.refresh?.token) {
    localStorage.setItem("refreshToken", tokens.refresh.token);
  }
  if (account?.id) localStorage.setItem("userId", account.id);

  // Deliberately does not write `localStorage.role`. It used to store "mentee"
  // unconditionally, which is wrong for anybody who chose a mentorship goal:
  // the server derives `["mentee","mentor"]` from the goals, and the 41
  // components that read the string turn it into a profile data key, so
  // `roleProfile("mentee")` quietly selected the wrong sub-document.
  //
  // The account contract is the single writer now (`capabilitySlice`), and
  // every protected route waits for it to load, so nothing renders against an
  // absent role in the meantime.
  clearOnboardingTokens();
  return true;
};

/* ---------------------------------------------------------------- fetch --- */

class OnboardingApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "OnboardingApiError";
    this.status = status;
  }
}

const apiUrl = (path) => `${import.meta.env.VITE_API_URL ?? ""}${path}`;

const expiredSessionError = (generation) => {
  const error = new OnboardingApiError("Onboarding session expired", 401);
  // A late response must never clear a newer login's credentials.
  error.sessionExpired = generation === onboardingSessionGeneration;
  if (error.sessionExpired) clearOnboardingTokens();
  return error;
};

const refreshOnboardingSessionOnce = async () => {
  const generation = onboardingSessionGeneration;
  const current = readOnboardingTokens();
  const refreshToken = current?.refresh?.token;
  if (!refreshToken) return false;

  const response = await fetch(apiUrl("/v2/auth/tokens/refresh"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const payload = await response.json().catch(() => null);
  const tokens = payload?.data?.tokens ?? payload?.tokens;
  // Logout or a newer account session may have replaced these credentials
  // while the refresh was in flight. Never restore an obsolete session.
  const latest = readOnboardingTokens();
  if (
    generation !== onboardingSessionGeneration ||
    latest?.refresh?.token !== refreshToken
  ) {
    return false;
  }

  if (response.status === 401) throw expiredSessionError(generation);
  if (!response.ok || !tokens?.access?.token) {
    // A failed refresh is not necessarily a rejected session. Preserve the
    // credentials and expose a retryable failure rather than the original 401.
    throw new OnboardingApiError(
      "Unable to refresh onboarding session. Try again.",
      503,
    );
  }

  writeOnboardingTokens({
    ...tokens,
    verifyEmailToken: latest?.verifyEmailToken ?? current.verifyEmailToken,
  });
  return true;
};

const refreshOnboardingSession = () => {
  if (!onboardingRefreshPromise) {
    const refreshPromise = refreshOnboardingSessionOnce();
    onboardingRefreshPromise = refreshPromise;
    const clearIfCurrent = () => {
      if (onboardingRefreshPromise === refreshPromise) {
        onboardingRefreshPromise = null;
      }
    };
    refreshPromise.then(clearIfCurrent, clearIfCurrent);
  }

  return onboardingRefreshPromise;
};

const call = async (
  path,
  { method = "GET", body, auth = false } = {},
  retry = true,
) => {
  const generation = onboardingSessionGeneration;
  const headers = { "Content-Type": "application/json" };
  let accessToken;

  if (auth) {
    accessToken = readOnboardingTokens()?.access?.token;
    if (!accessToken) {
      if (retry && (await refreshOnboardingSession())) {
        return call(path, { method, body, auth }, false);
      }
      throw expiredSessionError(generation);
    }
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(apiUrl(path), {
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);

  // Upload completion authenticates two independent credentials. These 401s
  // reject the upload reservation, not the member's session; retrying them
  // with refreshed login tokens cannot repair the reservation.
  if (
    response.status === 401 &&
    path === "/v1/web/upload/presign/complete" &&
    ["Upload token expired", "Upload token signature invalid"].includes(
      payload?.message,
    )
  ) {
    throw new OnboardingApiError(payload.message, response.status);
  }

  if (response.status === 401 && auth && retry) {
    // Another request may have finished rotating the token while this request
    // was in flight. Retry with that pair instead of consuming the old refresh
    // token a second time.
    const latestAccessToken = readOnboardingTokens()?.access?.token;
    if (latestAccessToken && latestAccessToken !== accessToken) {
      return call(path, { method, body, auth }, false);
    }

    if (await refreshOnboardingSession()) {
      return call(path, { method, body, auth }, false);
    }
  }

  if (!response.ok) {
    if (response.status === 401 && auth) {
      throw expiredSessionError(generation);
    }
    // Preserve the backend message for callers that need to identify a known
    // failure (such as a duplicate email); UI screens choose their own copy.
    throw new OnboardingApiError(
      payload?.message ?? `Request failed (${response.status})`,
      response.status,
    );
  }

  // Successful V2 responses are { status, message, data }.
  return payload?.data ?? payload;
};

/* ------------------------------------------------------------- endpoints -- */

/**
 * Upload a photo, in the only way `/v2/onboarding/complete` accepts.
 *
 * Profile uploads are portraits. The server stores the main portrait and any
 * additional portraits in the same profile-photo collection.
 *
 * Three steps, and none of them is optional:
 *
 *   1. `POST /v1/web/upload/presign`          reserves a key under
 *      `profile-pics/` and returns a presigned S3 form plus an upload token.
 *   2. `POST <s3 url>`                        the bytes, as a presigned POST
 *      form (the response carries `fields`, so this is not a PUT).
 *   3. `POST /v1/web/upload/presign/complete` tags the object with the
 *      uploader's `userId` and `fieldName=photo`, and returns the public URL.
 *
 * ## Why not the one-shot `/v1/web/upload/image`
 *
 * That endpoint is what this flow used to call, and it can never work here.
 * It routes to `uploadFileWeb`, which writes to the `web/` folder and applies
 * no ownership tags. `verifyOwnedProfilePhoto` requires both:
 *
 *     key under `profile-pics/`        else 400 "must be an Echo profile upload"
 *     tags.userId === caller           else 403 "does not belong to this account"
 *     tags.fieldName === "photo"
 *
 * So a member could upload a photo, see it render, and only discover at the
 * final screen that completion is impossible. Both failures were confirmed
 * against the dev API before this was written.
 *
 * ## Why not `baseFetch`
 *
 * It attaches `localStorage.token`, which is empty for the whole of onboarding
 * — the session lives in sessionStorage until completion — so every upload
 * would 401.
 */
export const uploadOnboardingPhoto = async (file) => {
  const presign = await call("/v1/web/upload/presign", {
    method: "POST",
    auth: true,
    body: {
      fieldName: "photo",
      mimetype: file.type,
      size: file.size,
      originalName: file.name,
    },
  });

  // `single` is a presigned POST form. The multipart mode exists for very large
  // files and needs per-part uploads; a profile photo is capped well below it,
  // so anything else means the contract moved and guessing would corrupt it.
  if (presign?.mode && presign.mode !== "single") {
    throw new OnboardingApiError(
      `Unsupported upload mode "${presign.mode}"`,
      500,
    );
  }

  const form = new FormData();
  for (const [key, value] of Object.entries(presign.fields ?? {})) {
    form.append(key, value);
  }
  // S3 requires the file last: everything after it in the form is ignored.
  form.append("file", file);

  const stored = await fetch(presign.url, { method: "POST", body: form });
  if (!stored.ok) {
    throw new OnboardingApiError("Photo upload failed", stored.status);
  }

  const completed = await call("/v1/web/upload/presign/complete", {
    method: "POST",
    auth: true,
    body: { key: presign.key, uploadToken: presign.uploadToken },
  });

  return completed?.url;
};

/** `GET /v2/auth/policies/current` — public, returns all three at once. */
export const fetchCurrentPolicies = async () => {
  const data = await call("/v2/auth/policies/current");
  return data?.policies ?? [];
};

/**
 * `POST /v2/auth/register`
 *
 * Note `dateOfBirth` is required here, not at completion: the age gate is
 * enforced server-side at account creation. `consents` must carry the exact
 * revision the member read, keyed by policy type.
 *
 * Returns `{ account, tokens, verifyEmailToken }`. Both the session and the
 * verification token are stashed — the OTP step needs the latter, and it is
 * not re-issued on a page refresh.
 */
export const registerAccount = async ({
  email,
  password,
  dateOfBirth,
  consents,
}) => {
  const data = await call("/v2/auth/register", {
    method: "POST",
    body: { email, password, dateOfBirth, consents },
  });

  startOnboardingSession({
    ...data.tokens,
    verifyEmailToken: data.verifyEmailToken,
  });

  return data.account;
};

/** `POST /v2/auth/otp/verify` — takes the register token, not the email. */
export const verifyEmailOtp = async (otp) => {
  const token = readOnboardingTokens()?.verifyEmailToken;
  if (!token) {
    throw new OnboardingApiError("Verification session expired", 401);
  }

  const data = await call("/v2/auth/otp/verify", {
    method: "POST",
    body: { token, otp },
  });

  // Verification mints a fresh session that is no longer restricted to
  // verifying. Invalidate any overlapping resend so it cannot attach another
  // challenge to the verified session.
  if (data?.tokens) {
    writeOnboardingTokens(
      { ...data.tokens, verifyEmailToken: undefined },
      { invalidatesOtp: true },
    );
  }

  return data;
};

/**
 * `POST /v2/auth/otp/send` — authenticated, and rate limited per user.
 * Replaces any outstanding challenge, so the newest code is the valid one.
 */
export const resendEmailOtp = () => {
  if (!onboardingOtpPromise) {
    const otpGeneration = onboardingOtpGeneration;
    const request = (async () => {
      const data = await call("/v2/auth/otp/send", {
        method: "POST",
        auth: true,
      });
      const verifyEmailToken = data?.verifyEmailToken?.trim();
      if (!verifyEmailToken) {
        throw new OnboardingApiError(
          "Verification request returned no token",
          500,
        );
      }
      if (otpGeneration !== onboardingOtpGeneration) {
        throw new OnboardingApiError("Onboarding session changed", 409);
      }
      if (!writeOnboardingTokenMetadata({ verifyEmailToken })) {
        throw new OnboardingApiError(
          "Verification session could not be saved",
          500,
        );
      }
      return data;
    })();

    onboardingOtpPromise = request;
    const clearIfCurrent = () => {
      if (onboardingOtpPromise === request) onboardingOtpPromise = null;
    };
    request.then(clearIfCurrent, clearIfCurrent);
  }

  return onboardingOtpPromise;
};

/** `POST /v2/onboarding/complete` — one transaction, requires a verified session. */
export const completeOnboarding = (payload) =>
  call("/v2/onboarding/complete", {
    method: "POST",
    body: payload,
    auth: true,
  });

/**
 * Complete onboarding once and install the app session it returns.
 *
 * Every caller in one onboarding session shares a single request. StrictMode
 * runs effects twice and a remount runs them again, and the server refuses a
 * second completion with a 409 (ECH-381). A failure clears it so a retry sends
 * again; success keeps it, so a later call resolves without sending.
 *
 * The app session is installed only if the onboarding session that sent the
 * request is still current, so a logout or a newer signup mid-request is not
 * overwritten.
 */
export const finishOnboarding = (payload) => {
  if (!finishPromise) {
    const session = onboardingSessionId;
    const attempt = completeOnboarding(payload).then((completion) => {
      if (session !== onboardingSessionId) {
        throw new OnboardingApiError(
          "Onboarding session ended before completion",
          401,
        );
      }
      if (!promoteToAppSession(completion)) {
        throw new OnboardingApiError(
          "Onboarding completion returned no app session",
          500,
        );
      }
      return completion;
    });

    finishPromise = attempt;
    attempt.catch(() => {
      if (finishPromise === attempt) finishPromise = null;
    });
  }

  return finishPromise;
};

export { OnboardingApiError };
